/**
 * User Management Types
 * Following the pattern from locationTypes.ts
 */

// ─── User Interface ───────────────────────────────────────────────────────────
export interface User {
  id: string;
  userEmail: string;
  location: string[];
  accessLevel: string;
  // Widened to string — Support-tier role names are fetched dynamically, not a fixed union.
  accessType?: string;
  gpid: string;
  locationId: string;
  organization?: string;
  department?: string;
  lastUpdateTime: string;
  naviJoinDate: string;
  activeStatus: 'Active' | 'Inactive';
  sustainTeamMember: boolean | null; // true = enabled, false = disabled, null = not set
  sustainTeamRole?: 'Member' | 'Lead' | null; // Only present when sustain member is/was enabled
  fundamentals: string | null;
  lighthouse: string | null;
  advFundamentals: string | null;
  newStatus: boolean;
}

/**
 * User Update Payload
 * 
 * For Super User / Developer:
 * - Send: role, activeStatus, locationMappings (roleId only)
 * - Do NOT send: accessType, organization, department, locations
 * 
 * For Normal User:
 * - Send: role, accessType, organization, department, activeStatus, locationMappings, locations
 */
export interface UserUpdatePayload {
  role?: string;
  // Widened to string — Support-tier role names are fetched dynamically, not a fixed union.
  accessType?: string;
  organization?: string;
  department?: string;
  activeStatus?: boolean;
  locations?: Array<{
    location: string;
    activeStatus: boolean;
  }>;
  locationMappings?: Array<{
    roleId: number;
    scopeType?: 'SITE' | 'MARKET' | 'REGION';
    locationId?: number;
    scopeRegion?: string;
    scopeMarket?: string;
  }>;
  sustainTeamMember?: boolean | null;
  sustainTeamRole?: 'Member' | 'Lead' | null;
  fundamentals?: string | null;
  lighthouse?: string | null;
  advFundamentals?: string | null;
  newStatus?: boolean;
  updatedBy?: string;
}

export interface UserUpdateRequest {
  userEmail: string;
  user: User;
  payload: UserUpdatePayload;
}

// ─── Filter State ─────────────────────────────────────────────────────────────
export interface FilterState {
  search: string;
  accessLevel: string[];
  location: string[];
  activeStatus: string[];
  sustainTeamMember: string[];
  newStatus: boolean | null;
}

export const DEFAULT_FILTERS: FilterState = {
  search: '',
  accessLevel: [],
  location: [],
  activeStatus: [],
  sustainTeamMember: [],
  newStatus: null,
};

// ─── Sort Configuration ───────────────────────────────────────────────────────
export interface SortConfig {
  column: keyof User | null;
  direction: 'asc' | 'desc';
}

export const DEFAULT_SORT: SortConfig = {
  column: null,
  direction: 'asc',
};

// ─── Modal States ─────────────────────────────────────────────────────────────
export type TrainingType = 'fundamentals' | 'lighthouse' | 'advFundamentals';

export interface TrainingModalState {
  visible: boolean;
  trainingType: TrainingType | null;
  selectedCount: number;
  overwriteCount: number;
}

export interface NewUsersModalState {
  visible: boolean;
  selectedCount: number;
}

// ─── User Management View ─────────────────────────────────────────────────────
export type UserManagementView = 'list' | 'detail';

// ─── Access Level Options ─────────────────────────────────────────────────────
export const ACCESS_LEVELS = [
  'Super User',
  'COE',
  'Region Leader/Division',
  'Site Leader',
  'Site Supervisor',
  'Certifier',
  'Checker',
  'Inventory Coordinator',
  'Team Lead',
  'Developer',
] as const;

// ─── Location Options ─────────────────────────────────────────────────────────
export const LOCATIONS = [
  'Columbia DC',
  'Dallas North DC',
  'Phoenix Production',
  'Chicago Manufacturing',
  'Los Angeles DC',
  'Atlanta DC',
  'Denver DC',
  'Seattle DC',
  'Boston DC',
  'Miami DC',
];

// ─── Status Options ───────────────────────────────────────────────────────────
export const STATUS_OPTIONS = ['Active', 'Inactive'] as const;

// ─── Training Field Mapping ───────────────────────────────────────────────────
export const TRAINING_FIELDS: Record<TrainingType, keyof User> = {
  fundamentals: 'fundamentals',
  lighthouse: 'lighthouse',
  advFundamentals: 'advFundamentals',
};

export const TRAINING_LABELS: Record<TrainingType, string> = {
  fundamentals: 'Fundamentals',
  lighthouse: 'Lighthouse',
  advFundamentals: 'Advanced Fundamentals',
};
